import { DisplayColumnDef } from "@tanstack/react-table";

import { PurchasingNote, PurchasingNoteCell } from "./PurchasingNoteCell";

export interface PurchasingNoteTableRow {
  jobId: number;

  /**
   * The initial note to display when the row is first loaded. Undefined indicates that the initial state is still
   * loading, null indicates that there is no note for this item/job combination. An error object indicates that there
   * was an error loading the note.
   */
  initialNote: PurchasingNote | Error | null | undefined;
}

export interface PurchasingNoteColumnDefOptions<TRow extends PurchasingNoteTableRow> {
  getItemId: (row: TRow) => number;
}

export function getPurchasingNoteColumnDef<TRow extends PurchasingNoteTableRow>({
  getItemId,
}: PurchasingNoteColumnDefOptions<TRow>): DisplayColumnDef<TRow> {
  return {
    id: "notes",
    header: "Notes",
    cell: (cell) => {
      const row = cell.row.original;
      if (row.initialNote === undefined) return null;

      if (row.initialNote instanceof Error) {
        return <span className="text-danger">Failed to load notes: {row.initialNote.message}</span>;
      }

      return <PurchasingNoteCell initialNote={row.initialNote} jobId={row.jobId} itemId={getItemId(row)} />;
    },
    meta: {
      headerCellProps: { className: "border-start-double" },
      cellProps: (cell) => {
        const noteLoading = cell.row.original.initialNote === undefined;
        return {
          className: `border-start-double ${noteLoading ? " skeleton-loading-table-cell" : "p-0"}`,
        };
      },
    },
  };
}
